import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export enum WarehouseTableConfigPage {
  WAREHOUSE_OPERATION = 'WAREHOUSE_OPERATION',
}

export interface ColumnConfigItem {
  label: string;
  key: string;
  order: number;
}

export interface FieldConfigDto {
  data: ColumnConfigItem[];
}

export interface CreateWarehouseTableConfigDto {
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault?: boolean;
}

export interface UpdateWarehouseTableConfigDto {
  name?: string;
  fieldConfigJson?: FieldConfigDto;
  isDefault?: boolean;
}

export interface ApplyWarehouseTableConfigDto {
  configId: number;
  page: WarehouseTableConfigPage;
}

export interface WarehouseTableConfig {
  id: number;
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault: boolean;
  page?: WarehouseTableConfigPage;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

export interface CurrentWarehouseTableConfigResponse {
  config: WarehouseTableConfig;
  mappingId: number | null;
  appliedAt: string | null;
  isDefault?: boolean;
}

const warehouseTableConfigApi = createApi({
  reducerPath: 'warehouseTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['WarehouseTableConfig'],
  endpoints: builder => ({
    getWarehouseTableConfigList: builder.query<
      BackendResponse<WarehouseTableConfig[]>,
      { page: WarehouseTableConfigPage }
    >({
      query: params => ({
        url: `/warehouse-table-config/config-list${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseTableConfig'],
    }),
    getWarehouseTableConfig: builder.query<
      BackendResponse<WarehouseTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/warehouse-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'WarehouseTableConfig', id },
      ],
    }),
    createWarehouseTableConfig: builder.mutation<
      BackendResponse<WarehouseTableConfig>,
      CreateWarehouseTableConfigDto & { page: WarehouseTableConfigPage }
    >({
      query: payload => ({
        url: '/warehouse-table-config/create-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['WarehouseTableConfig'],
    }),
    updateWarehouseTableConfig: builder.mutation<
      BackendResponse<WarehouseTableConfig>,
      { id: number; data: UpdateWarehouseTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/warehouse-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['WarehouseTableConfig'],
    }),
    getCurrentWarehouseTableConfig: builder.query<
      BackendResponse<CurrentWarehouseTableConfigResponse>,
      { page: WarehouseTableConfigPage }
    >({
      query: params => ({
        url: `/warehouse-table-config/current-config-page${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['WarehouseTableConfig'],
    }),
    applyWarehouseTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplyWarehouseTableConfigDto
    >({
      query: payload => ({
        url: `/warehouse-table-config/apply`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['WarehouseTableConfig'],
    }),
    deleteWarehouseTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/warehouse-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['WarehouseTableConfig'],
    }),
  }),
});

export const {
  useGetWarehouseTableConfigListQuery,
  useGetWarehouseTableConfigQuery,
  useGetCurrentWarehouseTableConfigQuery,
  useCreateWarehouseTableConfigMutation,
  useUpdateWarehouseTableConfigMutation,
  useApplyWarehouseTableConfigMutation,
  useDeleteWarehouseTableConfigMutation,
} = warehouseTableConfigApi;

export default warehouseTableConfigApi;
